<template>
  <div class="cm-filter">
    <div class="cm-seg">
      <button
        v-for="f in formats"
        :key="f.key"
        :class="{ active: getCmFormat === f.key }"
        :disabled="f.disabled"
        :title="f.disabled ? $t('CM_FORMAT_SOON') : ''"
        @click="!f.disabled && CM_SET_FORMAT(f.key)"
      >
        {{ $t(f.label) }}
      </button> 
    </div>
    <div
      v-if="socials.length > 1"
      class="cm-select"
      v-click-outside="() => { socialOpen = false; }"
    >
      <span class="cm-select-label" :class="{ active: socialOpen }" @click="socialOpen = !socialOpen">
        <b>{{ $t('CM_SOCIAL') }}</b>
        {{ socialSummary }}
        <span class="expand"></span>
      </span>

      <div v-show="socialOpen" class="cm-select-menu">
        <div class="cm-chips">
          <span
            class="cm-chip"
            :class="{ active: !getCmSocials.length }"
            @click="pickAllSocials"
          >
            {{ $t('CM_ALL') }}
          </span>
          <span
            v-for="s in socials"
            :key="s"
            class="cm-chip"
            :class="{ active: isSocialActive(s) }"
            @click="toggleSocial(s)"
          >
            {{ socialLabel(s) }}
          </span>
        </div>
      </div>
    </div>

    <div
      v-if="showScopeFilters && owners.length > 1"
      class="cm-select"
      v-click-outside="() => { ownerOpen = false; }"
    >
      <span class="cm-select-label" :class="{ active: ownerOpen }" @click="ownerOpen = !ownerOpen">
        <b>{{ $t('CM_OWNER') }}</b>
        {{ ownerSummary }}
        <span class="expand"></span>
      </span>

      <div v-show="ownerOpen" class="cm-select-menu">
        <div class="cm-chips">
          <span
            class="cm-chip"
            :class="{ active: !getCmOwner }"
            @click="pickOwner(null)"
          >
            {{ $t('CM_ALL') }}
          </span>
          <span
            v-for="o in owners"
            :key="o"
            class="cm-chip"
            :class="{ active: o === getCmOwner }"
            @click="pickOwner(o)"
          >
            {{ o }}
          </span>
        </div>
      </div>
    </div>

    <div
      v-if="showScopeFilters && categories.length"
      class="cm-select"
      v-click-outside="() => { categoryOpen = false; }"
    >
      <span class="cm-select-label" :class="{ active: categoryOpen }" @click="categoryOpen = !categoryOpen">
        <b>{{ $t('CM_CATEGORY') }}</b>
        {{ categorySummary }}
        <span class="expand"></span>
      </span>

      <div v-show="categoryOpen" class="cm-select-menu">
        <div class="cm-chips">
          <span
            class="cm-chip"
            :class="{ active: getCmCategory == null }"
            @click="pickCategory(null)"
          >
            {{ $t('CM_ALL') }}
          </span>
          <span
            v-for="c in categories"
            :key="c.id"
            class="cm-chip"
            :class="{ active: c.id === getCmCategory }"
            @click="pickCategory(c.id)"
          >
            {{ c.name }}
          </span>
        </div>
      </div>
    </div>

    <div
      class="cm-select"
      v-click-outside="() => { open = false; }"
    >
      <span class="cm-select-label" :class="{ active: open }" @click="open = !open">
        <b>{{ $t('CM_WORDS') }}</b>
        {{ summary }}
        <span class="expand"></span>
      </span>

      <div v-show="open" class="cm-select-menu cm-words-panel">
        <div class="cm-words-group">
          <div class="cm-words-title">
            {{ $t('CM_WORDS_TRACKED') }}
            <span style="float: right">{{ getCmAdded.length }}/{{ trackedMax }}</span>
          </div>
          <div class="cm-chips">
            <span v-for="w in getCmAdded" :key="w" class="cm-chip is-tracked">
              ★ {{ w }}
              <span class="cm-chip-x" @click.stop="remove('added', w)">&times;</span>
            </span>
          </div>
          <div class="cm-words-add">
            <input
              v-model="addedInput"
              type="text"
              maxlength="128"
              :disabled="trackedFull"
              :placeholder="trackedFull ? $t('CM_WORDS_LIMIT', { max: trackedMax }) : $t('CM_WORDS_PLACEHOLDER')"
              @keydown.enter.prevent="add('added')"
              @click.stop
            />
            <button class="btn-st-tr" :disabled="trackedFull" @click.stop="add('added')">{{ $t('ADD_PAGE_SELECT_PAGE') }}</button>
          </div>
        </div>

        <div class="cm-words-group">
          <div class="cm-words-title">{{ $t('CM_WORDS_EXCLUDED') }}</div>
          <div class="cm-chips">
            <span v-for="w in getCmExcluded" :key="w" class="cm-chip">
              {{ w }}
              <span class="cm-chip-x" @click.stop="remove('excluded', w)">&times;</span>
            </span>
          </div>
          <div class="cm-words-add">
            <input
              v-model="excludedInput"
              type="text"
              maxlength="128"
              :placeholder="$t('CM_WORDS_PLACEHOLDER')"
              @keydown.enter.prevent="add('excluded')"
              @click.stop
            />
            <button class="btn-st-tr" @click.stop="add('excluded')">{{ $t('ADD_PAGE_SELECT_PAGE') }}</button>
          </div>
        </div>

        <div v-if="lemmaNote" class="cm-words-note">{{ lemmaNote }}</div>
        <div v-if="getCmWordsError" class="cm-words-note is-error">{{ getCmWordsError }}</div>
        <div class="cm-words-note">{{ $t('CM_WORDS_NOTE') }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from "vuex";
import ClickOutside from "vue-click-outside";
import {
  CM_SET_FORMAT,
  CM_ADD_WORD,
  CM_REMOVE_WORD,
  CM_SET_OWNER,
  CM_SET_CATEGORY,
  CM_SET_SOCIALS,
} from "@/store/actions/contentMonitoring";

export default {
  name: "Cm-filters",
  directives: { ClickOutside },

  props: {
    // фильтры по владельцу/категории доступны только на главном экране (Intro),
    // где мониторинг идёт по всем страницам; из дашборда конкретной страницы —
    // скрыты, т.к. скоуп там уже задан выбранными страницами
    showScopeFilters: { type: Boolean, default: false },
  },

  data() {
    return {
      open: false,
      ownerOpen: false,
      categoryOpen: false,
      socialOpen: false,
      addedInput: "",
      excludedInput: "",
      lemmaNote: "",
      trackedMax: 50, // лимит отслеживаемых слов; сервер валидирует тем же числом
      // «Картинки» — аналитика по разбору картинок постов (MVP-7), «Всё» —
      // слова текста и картинок вместе; сервер различает их параметром format
      formats: [
        { key: "text", label: "CM_FORMAT_TEXT", disabled: false },
        { key: "media", label: "CM_FORMAT_MEDIA", disabled: false },
        { key: "all", label: "CM_FORMAT_ALL", disabled: false },
      ],
    };
  },

  computed: {
    ...mapGetters([
      "getCmFormat",
      "getCmAdded",
      "getCmExcluded",
      "getCmWordsError",
      "getCmOwner",
      "getCmCategory",
      "getCmSocials",
      "getPages",
      "getSelectedPages",
      "getCategories",
    ]),

    /** Лимит отслеживаемых слов исчерпан — ввод в этот список блокируем. */
    trackedFull() {
      return this.getCmAdded.length >= this.trackedMax;
    },

    summary() {
      const total = this.getCmAdded.length + this.getCmExcluded.length;
      return total ? `${this.getCmAdded.length} ★ / ${this.getCmExcluded.length} 🚫` : this.$t("CM_WORDS_EMPTY");
    },

    owners() {
      const seen = new Set();
      const list = [];
      this.getPages.forEach((p) => {
        if (p.email && !seen.has(p.email)) {
          seen.add(p.email);
          list.push(p.email);
        }
      });
      return list;
    },

    /** Категории, в которых есть хотя бы одна страница. */
    categories() {
      return this.getCategories.filter((c) => {
        const ids = c.pages ? c.pages.map((p) => p.page_id) : c.page_ids || [];
        return ids.length > 0;
      });
    },

    socialPages() {
      return this.showScopeFilters ? this.getPages : this.getSelectedPages;
    },

    socials() {
      const order = ["FB", "IG", "VK", "TG"];
      const seen = new Set();
      (this.socialPages || []).forEach((p) => {
        const code = String(p.social || "").toUpperCase();
        if (code) seen.add(code);
      });
      return order.filter((s) => seen.has(s)).concat(
        Array.from(seen).filter((s) => order.indexOf(s) === -1),
      );
    },

    socialSummary() {
      if (!this.getCmSocials.length) return this.$t("CM_ALL");
      return this.getCmSocials.map((s) => this.socialLabel(s)).join(", ");
    },

    ownerSummary() {
      return this.getCmOwner || this.$t("CM_ALL");
    },

    categorySummary() {
      if (this.getCmCategory == null) return this.$t("CM_ALL");
      const found = this.categories.find((c) => c.id === this.getCmCategory);
      return found ? found.name : this.$t("CM_ALL");
    },
  },

  methods: {
    ...mapMutations([CM_SET_FORMAT, CM_SET_OWNER, CM_SET_CATEGORY, CM_SET_SOCIALS]),
    ...mapActions([CM_ADD_WORD, CM_REMOVE_WORD]),

    socialLabel(code) {
      const keys = {
        FB: "COMMON_SOCIALS_FB",
        IG: "COMMON_SOCIALS_IG",
        VK: "COMMON_SOCIALS_VK",
      };
      return keys[code] ? this.$t(keys[code]) : code === "TG" ? "Telegram" : code;
    },

    isSocialActive(code) {
      return this.getCmSocials.indexOf(code) !== -1;
    },

    pickAllSocials() {
      this.CM_SET_SOCIALS([]);
    },

    toggleSocial(code) {
      const current = this.getCmSocials.slice();
      const i = current.indexOf(code);
      if (i === -1) current.push(code);
      else current.splice(i, 1);
      if (!current.length || current.length === this.socials.length) {
        this.CM_SET_SOCIALS([]);
      } else {
        this.CM_SET_SOCIALS(current);
      }
    },

    pruneSocials() {
      const available = this.socials;
      const kept = this.getCmSocials.filter((s) => available.indexOf(s) !== -1);
      if (!kept.length || kept.length === available.length) {
        if (this.getCmSocials.length) this.CM_SET_SOCIALS([]);
      } else if (kept.length !== this.getCmSocials.length) {
        this.CM_SET_SOCIALS(kept);
      }
    },

    pickOwner(owner) {
      this.CM_SET_OWNER(owner);
      this.ownerOpen = false;
    },

    pickCategory(category) {
      this.CM_SET_CATEGORY(category);
      this.categoryOpen = false;
    },
    add(list) {
      const key = list === "added" ? "addedInput" : "excludedInput";
      const word = this[key].trim();
      if (!word) return;
      if (list === "added" && this.trackedFull) return;

      this.CM_ADD_WORD({ list, word, normalize: true })
        .then((stored) => {
          this.lemmaNote =
            stored && stored !== word.toLowerCase()
              ? this.$t("CM_WORDS_LEMMA", { word, stored })
              : "";
        })
        .catch(() => {});

      this[key] = "";
    },

    remove(list, word) {
      this.CM_REMOVE_WORD({ list, word });
    },
  },

  mounted() {
    if (this.showScopeFilters) {
      // категории нужны для дропдауна фильтра; на дашборде их могли ещё не грузить
      if (!this.getCategories.length) {
        this.$store.dispatch("CATEGORIES_SET_LIST");
      }
    } else {
      // без фильтров скоупа не оставляем висеть старый выбор из Intro,
      // иначе мониторинг конкретной страницы отфильтруется по чужому владельцу/категории
      if (this.getCmOwner) this.CM_SET_OWNER(null);
      if (this.getCmCategory != null) this.CM_SET_CATEGORY(null);
      this.pruneSocials();
    }
  },
};
</script>
